import React from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";

// Colour scheme mirrors the scope-type pills:
//   Execution → blue, Design → purple.
const EXECUTION_COLOR = "#4D6BF6";
const DESIGN_COLOR = "#8B5CF6";

const Label = ({ children, align = "left" }) => (
    <Typography
        sx={{
            color: "#949DAA",
            fontSize: "14px",
            fontWeight: 500,
            fontFamily: "Inter Tight",
            textAlign: align,
            whiteSpace: "nowrap",
        }}
    >
        {children}
    </Typography>
);

const Value = ({ children, align = "left", big = false }) => (
    <Typography
        sx={{
            fontWeight: big ? 700 : 600,
            fontSize: big ? "32px" : "18px",
            lineHeight: 1.1,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
            textAlign: align,
        }}
    >
        {children}
    </Typography>
);

// A rounded progress track holding one or more coloured segments.
const Bar = ({ segments }) => {
    const total = segments.reduce((s, seg) => s + (seg.value || 0), 0) || 1;
    return (
        <Box
            sx={{
                display: "flex",
                width: "100%",
                height: "10px",
                gap: "4px",
            }}
        >
            {segments.map((seg, i) => (
                <Box
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    sx={{
                        width: `${((seg.value || 0) / total) * 100}%`,
                        backgroundColor: seg.color,
                        borderRadius: "999px",
                        minWidth: seg.value ? "8px" : 0,
                    }}
                />
            ))}
        </Box>
    );
};

export default function BidStats({ stats, loading = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    if (loading && !stats) {
        return (
            <Box
                sx={{
                    border: "1px solid #EEF0F3",
                    borderRadius: "12px",
                    height: isMobile ? 300 : 120,
                    backgroundColor: "#FBFCFE",
                }}
            />
        );
    }
    if (!stats) return null;

    const execution = stats.execution_count || 0;
    const design = stats.design_count || 0;
    const proposed = stats.proposed_to_client || 0;
    const ready = stats.ready_to_start || 0;
    const ongoing = stats.ongoing || 0;

    // Each section: a labels row, a values row, then (optionally) a bar —
    // rows aligned across sections so the labels sit on one line.
    const totalSection = (
        <Box className="stat-section" sx={{ minWidth: 130 }}>
            <Label>Total Count</Label>
            <Box sx={{ marginTop: "8px" }}>
                <Value big>{stats.total_count ?? 0}</Value>
            </Box>
        </Box>
    );

    const typeSection = (
        <Box className="stat-section" sx={{ flex: 1, minWidth: 220 }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                }}
            >
                <Label>Execution</Label>
                <Label align="right">Design</Label>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                }}
            >
                <Value>{execution}</Value>
                <Value align="right">{design}</Value>
            </Box>
            <Bar
                segments={[
                    { value: execution, color: EXECUTION_COLOR },
                    { value: design, color: DESIGN_COLOR },
                ]}
            />
        </Box>
    );

    const progressSection = (
        <Box className="stat-section" sx={{ flex: 1.3, minWidth: 260 }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                }}
            >
                <Label>Proposed</Label>
                <Label align="center">Ready to start</Label>
                <Label align="right">Ongoing</Label>
            </Box>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                }}
            >
                <Value>{proposed}</Value>
                <Value align="center">{ready}</Value>
                <Value align="right">{ongoing}</Value>
            </Box>
            <Bar
                segments={[
                    { value: proposed, color: "#4D6BF6" },
                    { value: ready, color: "#22C55E" },
                    { value: ongoing, color: "#F59E0B" },
                ]}
            />
        </Box>
    );

    const vDivider = (
        <Box sx={{ width: "1px", alignSelf: "stretch", backgroundColor: "#EEF0F3" }} />
    );
    const hDivider = (
        <Box sx={{ height: "1px", width: "100%", backgroundColor: "#EEF0F3" }} />
    );

    return (
        <Box
            sx={{
                // Only a bottom border; inner dividers run down to touch it.
                borderBottom: "1px solid #EEF0F3",
                backgroundColor: "transparent",
                paddingTop: isMobile ? "4px" : "6px",
                paddingBottom: 0,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "stretch",
                gap: isMobile ? "16px" : "14px",
                // Bottom padding lives on the sections (not the dividers) so
                // the dividers stretch all the way to the bottom border.
                "& > .stat-section": {
                    paddingBottom: isMobile ? "16px" : "20px",
                },
            }}
        >
            {totalSection}
            {isMobile ? hDivider : vDivider}
            {typeSection}
            {isMobile ? hDivider : vDivider}
            {progressSection}
        </Box>
    );
}
